import React, { useState } from 'react';
import { AccountsTableContext, IAccountsTableContext } from './AccountsTableContext';
import { useAsyncData } from '@shared/react';
import { useErrorNotify } from '@features/useErrorNotify';
import { getAccounts } from '../../api';
import { ISettingsAccountsDto } from '@shared/api';
import { useDrawer, useModal } from '@features/Modal';
import { AccountsActionModal, AccountsViewDrawer } from '../../components';
import { AccountAction } from '../';
import { usePagination } from '@features/Pagination';
import { combineFilterWithOp } from '@shared/utils';
import { VerificationModal } from '@features/VerificationModal';

export interface IAccountsDesc {
    id: boolean;
    clearingAccount: boolean;
    fdid: boolean;
    cash: boolean;
    baseCash: boolean;
    [key: string]: boolean;
}

export const AccountsTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [clearingAccount, setCan] = useState<string>('');
    const [fdid, setFdid] = useState<string>('');
    const [accountStatuses, setAccountStatuses] = useState<string[]>([]);
    const [accountMarginTypes, setAccountMarginTypes] = useState<string[]>([]);
    const [isError, setIsError] = useState<string>('false');
    const [isInventory, setIsInventory] = useState<string>('false');

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination();

    const [sortFieldName, setSortFieldName] = useState('id');

    const [desc, setDesc] = useState<IAccountsDesc>({
        id: false,
        clearingAccount: true,
        fdid: true,
        cash: true,
        baseCash: true,
    });

    const {
        data: accounts,
        loading: accountsLoading,
        error: accountsError,
        handleRefresh: accountsRefresh,
    } = useAsyncData(
        async () => {
            const filter = {
                clearingAccount: { value: clearingAccount, operator: '=*' },
                fdid: { value: fdid, operator: '=*' },
                status: { value: accountStatuses.length && ` ${accountStatuses.join(';')}`, operator: ' #In' },
                marginType: {
                    value: accountMarginTypes.length && ` ${accountMarginTypes.join(';')}`,
                    operator: ' #In',
                },
                isError: { value: isError, operator: ' #In' },
                isInventory: { value: isInventory, operator: ' #In' },
            };
            const { items, totalResults } = await getAccounts({
                page: page,
                pageSize,
                filter: combineFilterWithOp(filter),
                sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
            });
            setTotalCount(totalResults);
            return items || [];
        },
        [],
        [page, pageSize, clearingAccount, fdid, accountStatuses, accountMarginTypes, isError, isInventory],
    );

    const handleAccountsSort = (fieldName: string) => {
        setSortFieldName(fieldName);
        setDesc((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
        accountsRefresh();
    };

    useErrorNotify(accountsError, 'Records');

    const { open: openAccountsViewModal } = useDrawer(AccountsViewDrawer);
    const { open: openAccountsActionModal } = useModal(AccountsActionModal);

    const onAccountViewClick = (account: ISettingsAccountsDto) => {
        openAccountsViewModal({
            account,
            VerificationModal,
            accountsRefresh,
            localStorageKey: 'isAccountChanged',
        });
    };

    const onAccountActionClick = (
        account: ISettingsAccountsDto,
        action: AccountAction,
        refresh: () => void,
        AdditionalComponent?: React.FC | null,
    ) => {
        openAccountsActionModal({ account, action, refresh, AdditionalComponent });
    };

    const value: IAccountsTableContext = {
        accounts,
        accountStatuses,
        accountMarginTypes,
        accountsLoading,
        paginationOptions,
        desc,
        sortFieldName,
        isError,
        isInventory,
        handleAccountsSort,
        accountsRefresh,
        onAccountViewClick,
        onAccountActionClick,
        setCan,
        setFdid,
        setAccountMarginTypes,
        setAccountStatuses,
        setIsError,
        setIsInventory,
    };

    return <AccountsTableContext.Provider value={value}>{children}</AccountsTableContext.Provider>;
};
